import React, { useState, createContext, useContext } from "react";
import { Toast, ToastContainer } from "react-bootstrap";
import "./Alert.css";

interface Alert {
  id: number;
  variant: string;
  title: string;
  message: string;
  position: "top-start" | "top-end" | "bottom-start" | "bottom-end";
  duration: number;
}

const AlertContext = createContext<
  (variant: string, title: string, message: string, position?: Alert["position"], duration?: number) => void
>(() => {});

const AlertProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [alerts, setAlerts] = useState<Alert[]>([]);

  const showAlert = (
    variant: string,
    title: string,
    message: string,
    position: Alert["position"] = "top-end",
    duration: number = 3000
  ) => {
    const id = new Date().getTime();
    setAlerts((prev) => [...prev, { id, variant, title, message, position, duration }]);

    setTimeout(() => {
      setAlerts((prev) => prev.filter((alert) => alert.id !== id));
    }, duration);
  };

  return (
    <AlertContext.Provider value={showAlert}>
      {children}
      {["top-start", "top-end", "bottom-start", "bottom-end"].map((pos) => (
        <ToastContainer key={pos} className="p-3 toast-container" position={pos as any}>
          {alerts
            .filter((alert) => alert.position === pos)
            .map((alert) => (
              <Toast
                key={alert.id}
                bg={alert.variant}
                onClose={() => setAlerts((prev) => prev.filter((a) => a.id !== alert.id))}
                className={`toast bg-${alert.variant}`}
              >
                <Toast.Header>
                  <strong className="me-auto">{alert.title}</strong>
                </Toast.Header>
                <Toast.Body>{alert.message}</Toast.Body>
              </Toast>
            ))}
        </ToastContainer>
      ))}
    </AlertContext.Provider>
  );
};

export const useAlert = () => useContext(AlertContext);

export default AlertProvider;
